import { useEffect, useState } from "react";
import { Package } from "lucide-react";
import * as magazzinoApi from "../../api/magazzino";
import {
  OrdineMagazzino,
  StatoOrdineMagazzino,
  STATO_ORDINE_LABELS,
  UbicazioneMagazzino,
  UBICAZIONE_LABELS,
} from "../../api/magazzino";
import * as catalogoApi from "../../api/catalogo";
import { DispositivoCatalogo } from "../../api/catalogo";
import { Cliente } from "../../api/clients";
import { getErrorMessage } from "../../api/client";
import { useToast } from "../../context/ToastContext";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { ClienteAutocomplete } from "../../components/ui/ClienteAutocomplete";

/**
 * Registra o modifica un ordine a fornitore.
 *
 * ## Le due modalità del cliente
 *
 * Un ordine può andare a un cliente **già in anagrafica** oppure a uno
 * **nuovo**, di cui il commerciale ha solo nome e P.IVA scritti a mano. Sono
 * mutuamente esclusive per costruzione — un interruttore, non due campi che si
 * possono riempire insieme — perché nello schema riempirli entrambi produce una
 * riga che dice due cose diverse sullo stesso ordine.
 *
 * ## Perché "confermato" a volte non si può scegliere
 *
 * Un ordine intestato a un cliente che non esiste ancora **non può essere
 * confermato**: nessuno potrebbe fatturarlo. La regola vive nel backend
 * (`utils/ordineMagazzino.ts`) e qui viene solo *anticipata*, disabilitando
 * l'opzione invece di lasciarla scegliere e poi rifiutare — ma il controllo
 * vero resta là, perché è là che passano anche le chiamate che non vengono da
 * questo form.
 *
 * Il caso "nessun cliente" resta confermabile: è merce ordinata a scorta.
 *
 * ## Che cosa NON c'è qui
 *
 * La **matricola**. Non è un campo dell'ordine: è l'identità del pezzo fisico,
 * vive su `devices.matricola` (UNIQUE) e nasce quando il fornitore la comunica,
 * che può essere adesso come fra due mesi. Si assegna dall'azione dedicata
 * sulla riga — vedi `AssegnaMatricola.tsx`.
 *
 * Anche `stato_match_cliente` non c'è: è un esito che calcola il backend
 * confrontando la P.IVA con l'anagrafica, non una cosa che si dichiara.
 */
export function OrdineForm({
  ordine,
  onChiudi,
  onSalvato,
}: {
  /** Assente = nuovo ordine. */
  ordine?: OrdineMagazzino;
  onChiudi: () => void;
  onSalvato: () => void;
}) {
  const { showToast } = useToast();
  const inModifica = Boolean(ordine);

  const [catalogo, setCatalogo] = useState<DispositivoCatalogo[]>([]);
  const [catalogoId, setCatalogoId] = useState(ordine?.dispositivo_catalogo?.id ?? "");
  const [quantita, setQuantita] = useState(String(ordine?.quantita ?? 1));
  const [dataOrdine, setDataOrdine] = useState(ordine?.data_ordine?.slice(0, 10) ?? "");
  const [odooRef, setOdooRef] = useState(ordine?.odoo_order_ref ?? "");
  const [statoOrdine, setStatoOrdine] = useState<StatoOrdineMagazzino>(ordine?.stato_ordine ?? "da_confermare");

  /**
   * Dove sta la merce. In creazione parte da `presso_fornitore`, che non e' un
   * ripiego: un ordine appena registrato e' roba che qualcun altro ha ancora in
   * mano. Spostarla dopo si fa dall'azione dedicata sulla riga, che gestisce
   * anche il centro assistenza — qui resta la scelta iniziale.
   */
  const [ubicazione, setUbicazione] = useState<UbicazioneMagazzino>(ordine?.ubicazione ?? "presso_fornitore");

  /**
   * Quale delle due modalità è attiva. Si deduce dal record in modifica: se ha
   * un cliente collegato è "esistente", altrimenti "nuovo" — e un ordine senza
   * nessun cliente parte da "esistente" col campo vuoto, che è la strada
   * normale.
   */
  const [modo, setModo] = useState<"esistente" | "nuovo">(
    ordine && !ordine.cliente_preassegnato && (ordine.cliente_nuovo_nome || ordine.cliente_nuovo_piva)
      ? "nuovo"
      : "esistente"
  );
  const [clienteTesto, setClienteTesto] = useState(ordine?.cliente_preassegnato?.nome ?? "");
  const [clienteId, setClienteId] = useState(ordine?.cliente_preassegnato?.id ?? "");
  const [nuovoNome, setNuovoNome] = useState(ordine?.cliente_nuovo_nome ?? "");
  const [nuovaPiva, setNuovaPiva] = useState(ordine?.cliente_nuovo_piva ?? "");

  const [note, setNote] = useState(ordine?.note ?? "");
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);

  useEffect(() => {
    catalogoApi
      .listCatalogo()
      // Un catalogo che non si carica non deve bloccare il form: il modello è
      // facoltativo sull'ordine, quindi si resta senza opzioni e si prosegue.
      .then(({ dispositivi }) => setCatalogo(dispositivi))
      .catch(() => setCatalogo([]));
  }, []);

  /**
   * "Cliente nuovo" con qualcosa scritto dentro = cliente non ancora in
   * anagrafica, quindi l'ordine non è confermabile. Stessa condizione del
   * backend, tenuta volutamente identica: se una delle due cambia e l'altra no,
   * l'utente vede un'opzione che poi viene rifiutata.
   */
  const clienteNonInAnagrafica = modo === "nuovo" && Boolean(nuovoNome.trim() || nuovaPiva.trim());

  // Se si passa a "cliente nuovo" mentre "confermato" era selezionato, lo stato
  // torna indietro da solo invece di restare su un valore non più ammesso.
  useEffect(() => {
    if (clienteNonInAnagrafica && statoOrdine === "confermato") setStatoOrdine("da_confermare");
  }, [clienteNonInAnagrafica, statoOrdine]);

  async function salva(e: React.FormEvent) {
    e.preventDefault();
    setErrore(null);

    const q = Number(quantita);
    // `Number("")` vale 0, che è un intero plausibile: il vuoto si controlla
    // prima della conversione, non dopo.
    if (quantita.trim() === "" || !Number.isInteger(q) || q < 1) {
      setErrore("La quantità dev'essere un numero intero maggiore di zero.");
      return;
    }
    if (modo === "esistente" && clienteTesto.trim() && !clienteId) {
      setErrore("Scegli il cliente dai suggerimenti: scrivere il nome non basta a collegarlo.");
      return;
    }

    // I campi dell'altra modalità si azzerano, non si omettono: in modifica,
    // passare da "nuovo" a "esistente" deve cancellare il nome scritto a mano,
    // o la riga continuerebbe a portarselo dietro.
    const payload: magazzinoApi.OrdinePayload = {
      dispositivo_catalogo_id: catalogoId || null,
      quantita: q,
      data_ordine: dataOrdine || null,
      odoo_order_ref: odooRef.trim() || null,
      stato_ordine: statoOrdine,
      cliente_preassegnato_id: modo === "esistente" ? clienteId || null : null,
      cliente_nuovo_nome: modo === "nuovo" ? nuovoNome.trim() || null : null,
      cliente_nuovo_piva: modo === "nuovo" ? nuovaPiva.trim() || null : null,
      // `centro_assistenza_id` NON si manda da qui: il centro si sceglie
      // dall'azione "Sposta la merce", che e' l'unica che lo mostra. Se
      // qualcuno seleziona `centro_assistenza` in creazione, il backend rifiuta
      // chiedendo quale — ed e' il messaggio giusto.
      ubicazione,
      note: note.trim() || null,
    };

    setInCorso(true);
    try {
      if (ordine) await magazzinoApi.aggiornaOrdine(ordine.id, payload);
      else await magazzinoApi.creaOrdine(payload);
      showToast(inModifica ? "Ordine aggiornato." : "Ordine registrato.", "success");
      onSalvato();
      onChiudi();
    } catch (err) {
      setErrore(getErrorMessage(err, "Impossibile salvare l'ordine."));
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Modal
      open
      onClose={onChiudi}
      title={inModifica ? "Modifica ordine" : "Nuovo ordine dispositivi"}
      size="md"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onChiudi}>
            Annulla
          </Button>
          <Button type="submit" form="form-ordine-magazzino" loading={inCorso}>
            <Package className="h-4 w-4" aria-hidden="true" />
            {inModifica ? "Salva" : "Registra ordine"}
          </Button>
        </div>
      }
    >
      <form id="form-ordine-magazzino" onSubmit={salva} className="flex flex-col gap-4" noValidate>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="ord-modello" className="text-sm font-medium text-ink">
              Modello a catalogo
            </label>
            <select
              id="ord-modello"
              className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
              value={catalogoId}
              onChange={(e) => setCatalogoId(e.target.value)}
            >
              {/* Facoltativo: si ordina anche qualcosa che a catalogo non c'è. */}
              <option value="">Non indicato</option>
              {catalogo.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.marca} {d.modello}
                  {d.sigla ? ` (${d.sigla})` : ""}
                </option>
              ))}
            </select>
          </div>

          <Input
            label="Quantità"
            type="number"
            min={1}
            step={1}
            value={quantita}
            onChange={(e) => setQuantita(e.target.value)}
            required
          />

          <Input label="Data ordine" type="date" value={dataOrdine} onChange={(e) => setDataOrdine(e.target.value)} />

          <Input
            label="Riferimento ordine Odoo"
            value={odooRef}
            onChange={(e) => setOdooRef(e.target.value)}
            hint="Testo libero: il formato definitivo non è ancora stabilito."
          />
        </div>

        {/* --- Cliente: due modalità, mai insieme ------------------------- */}
        <fieldset className="flex flex-col gap-3 rounded-lg border border-line bg-line/10 p-3">
          <legend className="px-1 text-sm font-medium text-ink">Cliente</legend>

          <div className="flex flex-wrap gap-4">
            {(
              [
                ["esistente", "Già in anagrafica"],
                ["nuovo", "Nuovo, non ancora censito"],
              ] as const
            ).map(([valore, etichetta]) => (
              <label key={valore} className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                <input
                  type="radio"
                  name="modo-cliente"
                  value={valore}
                  checked={modo === valore}
                  onChange={() => setModo(valore)}
                  className="h-4 w-4 accent-primary"
                />
                {etichetta}
              </label>
            ))}
          </div>

          {modo === "esistente" ? (
            <div className="flex flex-col gap-1.5">
              <ClienteAutocomplete
                label="Cerca in anagrafica"
                value={clienteTesto}
                // Digitare a mano azzera la selezione: senza, resterebbe
                // agganciato un cliente che nel campo non si legge più.
                onChange={(v) => {
                  setClienteTesto(v);
                  setClienteId("");
                }}
                onSelect={(c: Cliente) => {
                  setClienteTesto(c.nome);
                  setClienteId(c.id);
                }}
              />
              <p className="text-xs text-ink/45">
                Lasciarlo vuoto è legittimo: è un ordine a scorta, non per qualcuno in particolare.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input label="Ragione sociale" value={nuovoNome} onChange={(e) => setNuovoNome(e.target.value)} />
              <Input
                label="P.IVA"
                value={nuovaPiva}
                onChange={(e) => setNuovaPiva(e.target.value)}
                hint="Alla prossima sincronizzazione con Odoo l'ordine si aggancia da solo, se la P.IVA corrisponde."
              />
            </div>
          )}
        </fieldset>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="ord-stato" className="text-sm font-medium text-ink">
            Stato ordine
          </label>
          <select
            id="ord-stato"
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            value={statoOrdine}
            onChange={(e) => setStatoOrdine(e.target.value as StatoOrdineMagazzino)}
          >
            {(Object.keys(STATO_ORDINE_LABELS) as StatoOrdineMagazzino[]).map((s) => (
              <option key={s} value={s} disabled={s === "confermato" && clienteNonInAnagrafica}>
                {STATO_ORDINE_LABELS[s]}
              </option>
            ))}
          </select>
          {clienteNonInAnagrafica && (
            <p className="text-xs text-ink/50">
              Un ordine intestato a un cliente non ancora in anagrafica resta «da confermare»: nessuno potrebbe
              fatturarlo. Si conferma dopo che il cliente è stato registrato.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="ord-ubicazione" className="text-sm font-medium text-ink">
            Dove si trova la merce
          </label>
          <select
            id="ord-ubicazione"
            className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
            value={ubicazione}
            onChange={(e) => setUbicazione(e.target.value as UbicazioneMagazzino)}
          >
            {(Object.keys(UBICAZIONE_LABELS) as UbicazioneMagazzino[]).map((u) => (
              <option key={u} value={u}>
                {UBICAZIONE_LABELS[u]}
              </option>
            ))}
          </select>
          {ubicazione === "centro_assistenza" && (
            <p className="text-xs text-ink/50">
              Quale centro si sceglie dall&apos;azione «Sposta la merce» sulla riga, dopo aver salvato.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="ord-note" className="text-sm font-medium text-ink">
            Note
          </label>
          <textarea
            id="ord-note"
            rows={2}
            className="rounded-lg border border-line bg-white px-3 py-2 text-sm text-ink"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {errore && <p className="text-sm font-medium text-accent">{errore}</p>}
      </form>
    </Modal>
  );
}
